
import { Helmet } from "react-helmet";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Briefcase, Home, Car, CreditCard, PiggyBank, 
  Wrench, Sailboat, Plane, DollarSign 
} from "lucide-react";
import PartnersCarousel from "@/components/products/PartnersCarousel";

const EnglishProductsPage = () => {
  const isMobile = useIsMobile();
  
  return (
    <>
      <Helmet>
        <title>All Loan Products | Lainasampo.fi</title>
        <meta name="description" content="Explore all loan products offered by Lainasampo.fi. Find the right loan for your needs easily." />
        <meta name="language" content="en" />
      </Helmet>
      
      <div className="bg-white">
        <div className="container mx-auto max-w-6xl px-4">
          {isMobile && (
            <div className="py-4">
              <Link to="/english/apply">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-lg shadow-md text-base flex items-center justify-center">
                  <span className="text-lg">Apply for a Loan</span>
                </Button>
              </Link>
            </div>
          )}
          
          {/* Hero Section with blue background */}
          <section className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-12 rounded-lg mb-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Our Loan Products</h1>
              <p className="text-xl md:text-2xl mb-4 text-blue-100">
                We offer a comprehensive range of loan products to meet your various financial needs. Compare options and find the perfect solution for you.
              </p>
              {!isMobile && (
                <Link to="/english/apply">
                  <Button className="bg-white text-blue-600 px-8 py-3 text-lg rounded-md hover:bg-blue-50 transition-colors">
                    Compare Loan Offers
                  </Button>
                </Link>
              )}
            </div>
          </section>
          
          {/* Products Grid */}
          <div className="py-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <ProductCard 
                title="Consumer Credit" 
                description="Flexible financing for everyday purchases and unexpected expenses."
                icon={<CreditCard className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Mortgage" 
                description="Affordable home financing options with competitive interest rates."
                icon={<Home className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Car Loan" 
                description="Tailored financing solutions for purchasing your dream vehicle."
                icon={<Car className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Debt Consolidation" 
                description="Simplify your finances by combining multiple loans into a single payment."
                icon={<PiggyBank className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Renovation Loan" 
                description="Finance your home improvement projects with flexible repayment options."
                icon={<Wrench className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Boat Loan" 
                description="Special financing for purchasing and maintaining boats and watercraft."
                icon={<Sailboat className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Holiday Loan" 
                description="Fund your dream vacation with easy repayment terms."
                icon={<Plane className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Business Loan" 
                description="Financial solutions for entrepreneurs and growing businesses."
                icon={<Briefcase className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
              <ProductCard 
                title="Flexible Credit" 
                description="Revolving credit line that's always available when you need it."
                icon={<DollarSign className="h-10 w-10 text-blue-600" />}
                link="/english/apply"
              />
            </div>
          </div>
          
          {/* Partners Section - Using PartnersCarousel with English language */}
          <PartnersCarousel altTitle="Our Trusted Partners" language="en" />
          
          {isMobile && (
            <div className="py-6 mb-8">
              <Link to="/english/apply">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-lg shadow-md text-base flex items-center justify-center">
                  <span className="text-lg">Apply Now</span>
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface ProductCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
}

const ProductCard = ({ title, description, icon, link }: ProductCardProps) => {
  return (
    <Link to={link}>
      <Card className="h-full transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
        <CardContent className="p-6">
          <div className="mb-4">
            {icon}
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
          <p className="text-gray-600">{description}</p>
        </CardContent>
      </Card>
    </Link>
  );
};

export default EnglishProductsPage;
